import { useCallback, useState } from 'react';
import { type StreamRecord } from './StreamEditor.types';

/*
    The course record the page edits.

    Most fields live in react-hook-form. Three do not, because no input owns them: the image, the
    locale pack, and the timestamps the server returns from a save. They are held here as overrides
    on top of the loaded record, so the save still sends one whole record.

    The page also needs to know whether any of them changed, because react-hook-form's isDirty
    cannot see them and the unsaved-changes warning would otherwise let them go without a word.
*/
export function useStreamRecord(loaded: StreamRecord | undefined) {
    const [overrides, setOverrides] = useState<Partial<StreamRecord>>({});
    const [dirty, setDirty] = useState(false);

    const record = loaded ? ({ ...loaded, ...overrides } as StreamRecord) : undefined;

    /*
        An edit the form cannot hold. It counts towards unsaved changes.

        Pass `dirties: false` for something the server has already stored — the SEO metadata saves on
        its own button, and marking the page dirty after that save asks the admin to confirm leaving
        a page with nothing left to save. It does not clear the flag either, because another
        unsaved change may still be outstanding.
    */
    const applyEdit = useCallback((change: Partial<StreamRecord>, { dirties = true } = {}) => {
        setOverrides(current => ({ ...current, ...change }));
        if (dirties) setDirty(true);
    }, []);

    // What came back from a successful save. It is the new baseline, not an edit.
    const applySaved = useCallback((saved: StreamRecord) => {
        setOverrides(current => ({ ...current, ...saved }));
        setDirty(false);
    }, []);

    return { record, recordDirty: dirty, applyEdit, applySaved };
}
